import type { Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Vehicle } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type PostBody = {
  firmware_release_id: string | undefined;
  skip_update_for_same_version_ecus?: boolean;
  ecu_names?: string[];
};

/**
 * Firmware/Agent更新開始リクエストAPI
 */
export const useSoftwareUpdateAPI = (): {
  isLoading: boolean;
  postSoftwareUpdate: (
    vehicleId: string,
    body?: PostBody,
  ) => Promise<Vehicle | undefined>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body?: PostBody,
    ): Promise<Result<Vehicle, AxiosResponse>> => {
      setIsLoading(true);
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/software/update`,
          body,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      } finally {
        setIsLoading(false);
      }
    },
    [fmsAPI],
  );

  const postSoftwareUpdate = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, body?: PostBody) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.post_software_update', { status: 'failed' }),
          });
          return;
        }
        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          body,
        );
        if (res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_software_update', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return;
        }
        notifySuccess({
          message: t('api.fms.post_software_update', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId:
              'vehicle_software_update_snackbar_success-status-div',
            closeButtonTestId:
              'vehicle_software_update_snackbar_success-click-close_button',
          },
        });
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    isLoading,
    postSoftwareUpdate,
  };
};
